import { useEffect, useId, useRef, useState } from 'react';
import type { FilterOptions, PricedTool } from '@ace-tracker/shared';
import { hasCostFilters, type CostFilters } from '../urlState';
import { Button } from './Button';
import { TOOL_LABELS } from './Sidebar';

const PRICED_TOOLS: PricedTool[] = ['claude-code', 'codex'];
const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue';

interface Option {
  value: string;
  hint?: string;
}

/**
 * Tool, model, project, and surface filters for the Spend, Efficiency and
 * Sessions views. Options come from the range's usage; each selection is a
 * validated query parameter (see dateRangeQuerySchema), never raw KQL.
 */
export function CostFilterBar({
  filters,
  options,
  onChange,
}: {
  filters: CostFilters;
  options: FilterOptions | undefined;
  onChange: (filters: CostFilters) => void;
}) {
  const set = (patch: Partial<CostFilters>) => onChange({ ...filters, ...patch });
  const toggleTool = (tool: PricedTool) =>
    set({ tools: filters.tools.includes(tool) ? filters.tools.filter((t) => t !== tool) : [...filters.tools, tool] });

  const models = dedupe((options?.models ?? []).map((m) => ({ value: m.model, hint: TOOL_LABELS[m.tool] })));
  const projects = dedupe((options?.projects ?? []).map((p) => ({ value: p.projectId, hint: TOOL_LABELS[p.tool] })));
  const surfaces = dedupe((options?.surfaces ?? []).map((s) => ({ value: s.surface, hint: TOOL_LABELS[s.tool] })));

  return (
    <section aria-label="Filters" className="rounded-card border border-border bg-surface px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-text-muted">Filters</span>
        <div role="group" aria-label="Tools" className="inline-flex rounded-pill border border-border bg-surface p-0.5">
          {PRICED_TOOLS.map((tool) => {
            const on = filters.tools.includes(tool);
            return (
              <button
                key={tool}
                type="button"
                aria-pressed={on}
                onClick={() => toggleTool(tool)}
                className={`min-h-[36px] rounded-pill px-3 text-xs font-medium transition-colors duration-150 ${focusRing} ${
                  on ? 'bg-primary-blue text-text-inverse' : 'text-text-muted hover:bg-surface-alt hover:text-text-default'
                }`}
              >
                {on && <i aria-hidden="true" className="fa-solid fa-check mr-1.5" />}
                {TOOL_LABELS[tool]}
              </button>
            );
          })}
        </div>
        <MultiSelect label="Model" options={models} selected={filters.models} onChange={(models) => set({ models })} />
        <MultiSelect label="Project" options={projects} selected={filters.projects} onChange={(projects) => set({ projects })} />
        <MultiSelect label="Surface" options={surfaces} selected={filters.surfaces} onChange={(surfaces) => set({ surfaces })} />
        {hasCostFilters(filters) && (
          <Button variant="text" className="!min-h-[36px] !text-xs" onClick={() => onChange({ tools: [], models: [], projects: [], surfaces: [] })}>
            Clear filters
          </Button>
        )}
      </div>
      {filters.surfaces.length > 0 && (
        <p className="mt-2 text-xs text-text-muted">Claude Code records carry a surface from collector 1.6.0 only, so older Claude Code usage is left out while a surface is selected.</p>
      )}
    </section>
  );
}

function dedupe(options: Option[]): Option[] {
  const byValue = new Map<string, Option>();
  for (const option of options) {
    const existing = byValue.get(option.value);
    byValue.set(option.value, existing && existing.hint !== option.hint ? { value: option.value, hint: 'Both tools' } : existing ?? option);
  }
  return [...byValue.values()];
}

function MultiSelect({ label, options, selected, onChange }: { label: string; options: Option[]; selected: string[]; onChange: (values: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A value selected from a shared link but absent from this range's options still shows, so it can be cleared.
  const all: Option[] = [...selected.filter((value) => !options.some((o) => o.value === value)).map((value) => ({ value })), ...options];
  const needle = query.trim().toLowerCase();
  const shown = needle ? all.filter((o) => o.value.toLowerCase().includes(needle)) : all;
  const toggle = (value: string) => onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-pill border px-3 text-xs font-medium transition-colors duration-150 ${focusRing} ${
          selected.length > 0 ? 'border-primary-blue text-primary-blue' : 'border-border text-text-muted hover:text-text-default'
        }`}
      >
        {label}
        <span className={selected.length > 0 ? 'font-bold' : 'font-normal'}>{selected.length > 0 ? `· ${selected.length}` : '· All'}</span>
        <i aria-hidden="true" className={`fa-solid ${open ? 'fa-chevron-up' : 'fa-chevron-down'} text-[10px]`} />
      </button>
      {open && (
        <div id={listId} className="absolute left-0 z-30 mt-1 w-72 rounded-card border border-border bg-surface p-2 shadow-lg">
          {all.length > 8 && (
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}s`}
              aria-label={`Search ${label.toLowerCase()}s`}
              className={`mb-2 w-full rounded-input border border-border bg-surface px-2 py-1.5 text-sm text-text-default ${focusRing}`}
            />
          )}
          <ul className="max-h-64 overflow-y-auto" aria-label={`${label} options`}>
            {shown.map((option) => (
              <li key={option.value}>
                <label className="flex min-h-[36px] cursor-pointer items-center gap-2 rounded-input px-2 text-sm hover:bg-surface-alt">
                  <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} className="h-4 w-4 accent-[var(--primary-blue)]" />
                  <span className="min-w-0 flex-1 truncate text-text-default" title={option.value}>{option.value}</span>
                  {option.hint && <span className="shrink-0 text-[11px] text-text-faint">{option.hint}</span>}
                </label>
              </li>
            ))}
            {shown.length === 0 && <li className="px-2 py-2 text-sm text-text-muted">No {label.toLowerCase()}s in this range.</li>}
          </ul>
          {selected.length > 0 && (
            <div className="mt-1 border-t border-border pt-1">
              <Button variant="text" className="!min-h-[32px] !text-xs" onClick={() => onChange([])}>
                Clear {label.toLowerCase()}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
